import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { statsAPI } from '../../services/api';
import { Package, ShoppingCart, DollarSign, FolderTree, AlertTriangle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await statsAPI.getDashboard();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Produits',
      value: stats?.products?.total || 0,
      icon: Package,
      color: 'cyan',
      link: '/admin/products'
    },
    {
      title: 'Commandes',
      value: stats?.orders?.total || 0,
      icon: ShoppingCart,
      color: 'green',
      link: '/admin/orders'
    },
    {
      title: 'Revenus',
      value: `${(stats?.revenue?.total || 0).toLocaleString('fr-DZ')} DA`,
      icon: DollarSign,
      color: 'yellow',
      link: null
    },
    {
      title: 'Catégories',
      value: stats?.categories?.total || 0,
      icon: FolderTree,
      color: 'purple',
      link: '/admin/categories'
    }
  ];

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-white mb-8">Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card) => {
            const Icon = card.icon;
            const content = (
              <div className="bg-blue-800 rounded-lg p-6 border border-blue-700">
                <div className="flex items-center justify-between mb-4">
                  <Icon className={`w-10 h-10 text-${card.color}-400`} />
                  {card.link && (
                    <TrendingUp className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <h3 className="text-gray-400 text-sm mb-1">{card.title}</h3>
                <p className="text-white text-2xl font-bold">{card.value}</p>
              </div>
            );

            return card.link ? (
              <Link key={card.title} to={card.link} className="block hover:scale-105 transition">
                {content}
              </Link>
            ) : (
              <div key={card.title}>{content}</div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Orders Status */}
          <div className="bg-blue-800 rounded-lg p-6 border border-blue-700">
            <h2 className="text-xl font-bold text-white mb-4">Statut des Commandes</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">En attente</span>
                <span className="text-yellow-400 font-bold">{stats?.orders?.pending || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Confirmées</span>
                <span className="text-blue-400 font-bold">{stats?.orders?.confirmed || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Livrées</span>
                <span className="text-green-400 font-bold">{stats?.orders?.delivered || 0}</span>
              </div>
            </div>
          </div>

          {/* Low Stock Alert */}
          <div className="bg-blue-800 rounded-lg p-6 border border-blue-700">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Stock Faible
            </h2>
            <p className="text-gray-400 mb-4">
              {stats?.products?.low_stock || 0} produit(s) avec un stock inférieur ou égal à 5 unités
            </p>
            <Link
              to="/admin/products"
              className="inline-block bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 transition"
            >
              Gérer les Produits
            </Link>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-white mb-4">Actions Rapides</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin/products/new"
              className="bg-cyan-500 text-white p-4 rounded-lg hover:bg-cyan-600 transition text-center font-semibold"
            >
              + Nouveau Produit
            </Link>
            <Link
              to="/admin/categories/new"
              className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition text-center font-semibold"
            >
              + Nouvelle Catégorie
            </Link>
            <Link
              to="/admin/orders"
              className="bg-green-500 text-white p-4 rounded-lg hover:bg-green-600 transition text-center font-semibold"
            >
              Voir les Commandes
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;